import { type FormEvent, useState } from "react";

import { api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { useLang } from "../lib/i18n";
import { authCopy } from "../lib/i18n/auth";
import { ScreenShell } from "./screen-shell";
import { Eyebrow, FormField, Input, MonoMeta, PrimaryButton } from "./ui/primitives";
import { SytoLogo } from "./ui/syto-logo";

// Onboarding screen, household creation after first sign-up.
// Layout per design_handoff_sytofood/screens-b.jsx → B_Onboarding:
// editorial display heading, monospaced eyebrow + numbered feature list,
// tangerine CTA stack at the bottom. Household creation form replaces the
// final "ZACZNIJMY" CTA from the design (form-flow vs. multi-step).
//
// Polish copy preserved from the design: peer-tone, "Jesz. Mówisz. My liczymy.",
// per design_handoff_sytofood/README.md §Polish-language copy.

export function OnboardingScreen() {
  const { refresh, signOut } = useAuth();
  const { lang } = useLang();
  const t = authCopy[lang];
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.createHousehold({ name: name.trim() });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.genericError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ScreenShell hideHeader>
      <div style={{ marginBottom: 20 }}>
        <SytoLogo size={26} />
      </div>

      <div style={{ marginTop: 28 }}>
        <Eyebrow>{t.obEyebrow}</Eyebrow>
        <h1
          style={{
            marginTop: 14,
            fontFamily: "var(--font-display)",
            fontSize: 52,
            fontWeight: 800,
            letterSpacing: "-0.05em",
            lineHeight: 0.92,
            color: "var(--ink)",
          }}
        >
          {t.obHeadingLine1}
          <br />
          {t.obHeadingLine2}
          <br />
          {t.obHeadingLine3}
          <span style={{ color: "var(--accent)" }}>.</span>
        </h1>
        <p
          style={{
            marginTop: 18,
            fontSize: 15,
            color: "var(--ink-2)",
            lineHeight: 1.5,
            maxWidth: 320,
          }}
        >
          {t.obIntro}
        </p>
      </div>

      <div style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 14 }}>
        {t.obFeatures.map((f) => (
          <div key={f.num} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--accent)",
                fontWeight: 700,
                paddingTop: 2,
                letterSpacing: "0.04em",
              }}
            >
              {f.num}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>{f.title}</div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{f.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3" style={{ marginTop: 32 }}>
        <Eyebrow>{t.obHouseholdEyebrow}</Eyebrow>
        <p style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5, marginTop: -2 }}>
          {t.obHouseholdIntro}
        </p>

        <FormField label={t.obNameLabel}>
          <Input
            type="text"
            required
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.obNamePlaceholder}
          />
        </FormField>

        {error ? (
          <p
            className="text-[12px] px-3 py-2 rounded-md"
            style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
          >
            {error}
          </p>
        ) : null}

        <PrimaryButton type="submit" disabled={submitting || !name.trim()} className="mt-1">
          {submitting ? t.obSubmitting : t.obSubmit}
        </PrimaryButton>

        <div className="flex justify-center" style={{ marginTop: 4 }}>
          <button
            type="button"
            onClick={() => {
              signOut().catch(() => null);
            }}
            style={{ color: "var(--ink-2)", fontSize: 13, fontWeight: 600 }}
          >
            {t.obSignOut}
          </button>
        </div>
      </form>

      <footer className="flex justify-center mt-auto" style={{ paddingTop: 32 }}>
        <MonoMeta>{t.obFooter}</MonoMeta>
      </footer>
    </ScreenShell>
  );
}
